import { Fragment, useEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon, CalendarDaysIcon } from "@heroicons/react/24/outline";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { InterviewSlots } from "../../Models";
import useApiToken from "../../hooks/useApiToken";
import useGraphApiToken from "../../hooks/useGraphApiToken";
import { Spinner } from "../Shared/Spinner";
import { GenerateMeetingLink, SearchUsers, GraphUser } from "../../api/microsoft.api";
import { updateInterviewSlot } from "../../api/roles.api";
import useClickOutside from "../../hooks/useClickOutside";

type SelectedInterviewer = {
  id: string;
  displayName: string;
  email: string;
};

interface RescheduleInterviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  interview: InterviewSlots;
  roleId: string;
  candidateId: string;
  onRescheduled: () => void;
}

export default function RescheduleInterviewModal({
  isOpen,
  onClose,
  interview,
  roleId,
  candidateId,
  onRescheduled,
}: RescheduleInterviewModalProps) {
  const token = useApiToken();
  const graphToken = useGraphApiToken();

  // ── Time fields: intentionally blank — recruiter must actively pick ──────
  const [newDate, setNewDate] = useState<Date | null>(null);
  const [newStartTime, setNewStartTime] = useState<Date | null>(null);
  const [newEndTime, setNewEndTime] = useState<Date | null>(null);

  // ── Pre-filled editable fields ────────────────────────────────────────────
  const [selectedInterviewers, setSelectedInterviewers] = useState<SelectedInterviewer[]>([]);
  const [notes, setNotes] = useState<string>("");

  // ── Interviewer search ────────────────────────────────────────────────────
  const [interviewerSearch, setInterviewerSearch] = useState("");
  const [searchResults, setSearchResults] = useState<GraphUser[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  useClickOutside(searchRef, () => setShowDropdown(false));

  // ── UX state ─────────────────────────────────────────────────────────────
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [notificationFailed, setNotificationFailed] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  // ── Initialise pre-filled fields when modal opens ────────────────────────
  useEffect(() => {
    if (!isOpen) return;

    // Reset time pickers (must stay blank)
    setNewDate(null);
    setNewStartTime(null);
    setNewEndTime(null);
    setSuccessMessage(null);
    setNotificationFailed(false);
    setErrorMessage(null);
    setValidationError(null);

    // Notes is email-only — always starts empty (nothing stored in DB to pre-fill)
    setNotes("");

    // Pre-fill interviewers from comma-separated email string
    if (interview.interviewerEmail) {
      const chips: SelectedInterviewer[] = interview.interviewerEmail
        .split(",")
        .map((email) => email.trim())
        .filter(Boolean)
        .map((email) => ({ id: email, displayName: email, email }));
      setSelectedInterviewers(chips);
    } else {
      setSelectedInterviewers([]);
    }
  }, [isOpen, interview]);

  // ── Load role stages removed — stage is not part of the reschedule flow ───

  // ── Interviewer search (MS Graph) ─────────────────────────────────────────
  useEffect(() => {
    if (!interviewerSearch.trim() || !graphToken) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }
    const debounce = setTimeout(async () => {
      setIsSearching(true);
      try {
        const results = await SearchUsers(graphToken, interviewerSearch);
        setSearchResults(results);
        setShowDropdown(results.length > 0);
      } catch {
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
    return () => clearTimeout(debounce);
  }, [interviewerSearch, graphToken]);

  const addInterviewer = (user: GraphUser) => {
    const email = user.mail || user.userPrincipalName;
    if (!email) return;
    if (selectedInterviewers.some((i) => i.email === email)) return;
    setSelectedInterviewers((prev) => [
      ...prev,
      { id: user.id, displayName: user.displayName, email },
    ]);
    setInterviewerSearch("");
    setShowDropdown(false);
    setSearchResults([]);
  };

  const removeInterviewer = (email: string) => {
    setSelectedInterviewers((prev) => prev.filter((i) => i.email !== email));
  };

  // ── Build combined DateTime from separate date + time pickers ─────────────
  const buildDateTime = (date: Date, time: Date): Date => {
    const combined = new Date(date);
    combined.setHours(time.getHours(), time.getMinutes(), 0, 0);
    return combined;
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSaveAndNotify = async () => {
    if (!token) return;
    setValidationError(null);
    setErrorMessage(null);

    // Validate: if any time field is set, all three must be set
    const anyTimeSet = newDate !== null || newStartTime !== null || newEndTime !== null;
    if (anyTimeSet && !(newDate && newStartTime && newEndTime)) {
      setValidationError("Please fill in the Date, Start Time, and End Time together, or leave all three blank.");
      return;
    }

    if (selectedInterviewers.length === 0) {
      setValidationError("At least one interviewer is required.");
      return;
    }

    let startTimeISO: string | undefined;
    let endTimeISO: string | undefined;
    let meetingLinkToSend: string | undefined = interview.meetingLink ?? undefined;
    const timeIsChanging = newDate !== null;

    // Generate a new Teams link only if the recruiter picked a new time
    if (timeIsChanging && newDate && newStartTime && newEndTime) {
      const combinedStart = buildDateTime(newDate, newStartTime);
      const combinedEnd = buildDateTime(newDate, newEndTime);

      if (combinedEnd <= combinedStart) {
        setValidationError("End time must be after start time.");
        return;
      }

      startTimeISO = combinedStart.toISOString();
      endTimeISO = combinedEnd.toISOString();

      if (graphToken) {
        setIsGeneratingLink(true);
        try {
          const newLink = await GenerateMeetingLink(
            graphToken,
            "Entnt Interview Meeting",
            combinedStart,
            combinedEnd
          );
          if (newLink) meetingLinkToSend = newLink;
        } catch {
          // Non-fatal: proceed with old link if generation fails
        } finally {
          setIsGeneratingLink(false);
        }
      }
    }

    setIsSaving(true);
    try {
      const result = await updateInterviewSlot(
        {
          slotId: interview.id,
          ...(startTimeISO && { startTime: startTimeISO }),
          ...(endTimeISO && { endTime: endTimeISO }),
          meetingLink: meetingLinkToSend,
          interviewerEmail: selectedInterviewers.map((i) => i.email).join(","),
          notes: notes.trim() || undefined,
          sendNotifications: true,
        },
        token
      );

      // ── Success message: derived entirely from backend response booleans.
      // candidateNotificationFailed / interviewerNotificationFailed distinguish
      // "send was attempted but threw" from "no send was needed at all".
      let msg: string;
      const {
        candidateNotified,
        candidateNotificationFailed,
        interviewerNotified,
        interviewerNotificationFailed,
      } = result;

      const anyFailed = candidateNotificationFailed || interviewerNotificationFailed;
      setNotificationFailed(anyFailed);
      if (candidateNotificationFailed && interviewerNotificationFailed) {
        // Both sends attempted, both failed
        msg =
          "Interview rescheduled, but neither the candidate nor the interviewer could be notified — please inform both manually.";
      } else if (candidateNotificationFailed && interviewerNotified) {
        // Candidate send failed, interviewer(s) all succeeded
        msg =
          "Interview rescheduled, but the candidate notification failed — please inform them of the new time manually.";
      } else if (candidateNotificationFailed) {
        // Candidate send failed, no interviewer send was needed (e.g. no interviewers on slot)
        msg =
          "Interview rescheduled, but the candidate notification failed — please inform them of the new time manually.";
      } else if (interviewerNotificationFailed && candidateNotified) {
        // Interviewer send(s) failed, candidate succeeded
        msg =
          "Interview rescheduled, but the interviewer notification failed — please inform them manually.";
      } else if (interviewerNotificationFailed) {
        // Interviewer send(s) failed, no candidate send was needed (Row 3 failure)
        msg =
          "Interview rescheduled, but the interviewer notification failed — please inform them manually.";
      } else if (candidateNotified && interviewerNotified) {
        // All sends succeeded
        msg = "Interview rescheduled. Candidate and interviewer notified.";
      } else if (candidateNotified) {
        // Only candidate was notified (no interviewers on slot, or Row 1 with no interviewers)
        msg = "Interview rescheduled. Candidate notified.";
      } else if (interviewerNotified) {
        // Row 3: interviewer-only change, all sends succeeded
        msg = "Interviewer updated. New interviewer notified.";
      } else {
        // Row 4: nothing changed that warranted a notification — no sends attempted
        msg = "Details saved. No notifications sent.";
      }
      setSuccessMessage(msg);

      setTimeout(() => {
        onRescheduled();
        handleClose();
      }, 1800);
    } catch (err: any) {
      setErrorMessage(err.message || "Failed to update interview slot. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (isSaving || isGeneratingLink) return; // block close during flight
    setNewDate(null);
    setNewStartTime(null);
    setNewEndTime(null);
    setSuccessMessage(null);
    setNotificationFailed(false);
    setErrorMessage(null);
    setValidationError(null);
    setInterviewerSearch("");
    setShowDropdown(false);
    onClose();
  };

  const isSubmitting = isGeneratingLink || isSaving;

  const buttonLabel = isGeneratingLink
    ? "Generating link…"
    : isSaving
    ? "Saving…"
    : "Save & Notify";

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-visible rounded-lg bg-white dark:bg-darkbglight px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                {/* Header */}
                <div className="absolute right-0 top-0 pr-4 pt-4">
                  <button
                    type="button"
                    className="rounded-md bg-white dark:bg-darkbglight text-gray-400 hover:text-gray-500"
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                    <CalendarDaysIcon className="h-6 w-6 text-blue-600" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-semibold leading-6 text-gray-900 dark:text-gray-300"
                    >
                      Reschedule Interview
                    </Dialog.Title>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      Current slot:{" "}
                      <span className="font-medium">
                        {new Date(interview.startTime).toLocaleString(undefined, {
                          dateStyle: "medium",
                          timeStyle: "short",
                        })}
                      </span>
                    </p>

                    <div className="mt-4 space-y-4">
                      {/* ── New Date (blank required) ───────────────────────── */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          New Date <span className="text-red-500">*</span>
                          <span className="ml-1 text-xs font-normal text-gray-400">(must pick a new value)</span>
                        </label>
                        <DatePicker
                          selected={newDate}
                          onChange={(date) => setNewDate(date)}
                          placeholderText="Pick a new date"
                          dateFormat="MMMM d, yyyy"
                          minDate={new Date()}
                          className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-darkbg dark:text-gray-300 shadow-sm focus:border-entntblue focus:ring-entntblue sm:text-sm"
                          autoComplete="off"
                        />
                      </div>

                      {/* ── Start & End Time ───────────────────────────────── */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Start Time
                          </label>
                          <DatePicker
                            selected={newStartTime}
                            onChange={(time) => setNewStartTime(time)}
                            showTimeSelect
                            showTimeSelectOnly
                            timeIntervals={15}
                            timeCaption="Start"
                            dateFormat="h:mm aa"
                            placeholderText="Pick start time"
                            className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-darkbg dark:text-gray-300 shadow-sm focus:border-entntblue focus:ring-entntblue sm:text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            End Time
                          </label>
                          <DatePicker
                            selected={newEndTime}
                            onChange={(time) => setNewEndTime(time)}
                            showTimeSelect
                            showTimeSelectOnly
                            timeIntervals={15}
                            timeCaption="End"
                            dateFormat="h:mm aa"
                            placeholderText="Pick end time"
                            className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-darkbg dark:text-gray-300 shadow-sm focus:border-entntblue focus:ring-entntblue sm:text-sm"
                          />
                        </div>
                      </div>

                      {/* ── Notes (pre-filled, editable) ───────────────────── */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Notes
                        </label>
                        <textarea
                          rows={3}
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          placeholder="Add notes about this interview…"
                          className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-darkbg dark:text-gray-300 shadow-sm focus:border-entntblue focus:ring-entntblue sm:text-sm"
                        />
                      </div>

                      {/* ── Interviewers (pre-filled, editable) ────────────── */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Interviewers
                        </label>

                        {/* Chips */}
                        {selectedInterviewers.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {selectedInterviewers.map((i) => (
                              <span
                                key={i.email}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200"
                              >
                                {i.displayName}
                                <button
                                  type="button"
                                  onClick={() => removeInterviewer(i.email)}
                                  className="hover:text-blue-600"
                                >
                                  <XMarkIcon className="h-3 w-3" />
                                </button>
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Search */}
                        <div ref={searchRef} className="relative">
                          <input
                            type="text"
                            placeholder="Search interviewers…"
                            value={interviewerSearch}
                            onChange={(e) => {
                              setInterviewerSearch(e.target.value);
                              setShowDropdown(true);
                            }}
                            className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-darkbg dark:text-gray-300 shadow-sm focus:border-entntblue focus:ring-entntblue sm:text-sm"
                          />
                          {isSearching && (
                            <div className="absolute right-2 top-2">
                              <Spinner />
                            </div>
                          )}
                          {showDropdown && searchResults.length > 0 && (
                            <ul className="absolute z-20 mt-1 w-full max-h-48 overflow-y-auto rounded-md bg-white dark:bg-darkbglight border border-gray-200 dark:border-gray-600 shadow-lg">
                              {searchResults.map((user) => (
                                <li
                                  key={user.id}
                                  className="flex flex-col px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                                  onClick={() => addInterviewer(user)}
                                >
                                  <span className="text-sm font-medium text-gray-900 dark:text-gray-200">
                                    {user.displayName}
                                  </span>
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    {user.mail || user.userPrincipalName}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>

                      {/* ── Validation error ──────────────────────────────── */}
                      {validationError && (
                        <p className="text-sm text-red-600 dark:text-red-400">{validationError}</p>
                      )}

                      {/* ── API error ─────────────────────────────────────── */}
                      {errorMessage && (
                        <div className="rounded-md bg-red-50 dark:bg-red-900/30 p-3 border border-red-200 dark:border-red-700">
                          <p className="text-sm text-red-700 dark:text-red-400">{errorMessage}</p>
                        </div>
                      )}

                      {/* ── Success / partial-failure message ─────────────── */}
                      {successMessage && (
                        <div
                          className={
                            notificationFailed
                              ? "rounded-md bg-amber-50 dark:bg-amber-900/30 p-3 border border-amber-200 dark:border-amber-700"
                              : "rounded-md bg-green-50 dark:bg-green-900/30 p-3 border border-green-200 dark:border-green-700"
                          }
                        >
                          <p
                            className={
                              notificationFailed
                                ? "text-sm text-amber-700 dark:text-amber-400"
                                : "text-sm text-green-700 dark:text-green-400"
                            }
                          >
                            {notificationFailed ? "⚠ " : "✓ "}{successMessage}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* ── Footer buttons ─────────────────────────────────────────── */}
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-2">
                  <button
                    type="button"
                    id="reschedule-save-notify-btn"
                    className="inline-flex w-full justify-center items-center gap-2 rounded-md bg-entntblue px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-entntorange sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleSaveAndNotify}
                    disabled={isSubmitting || !!successMessage}
                  >
                    {isSubmitting && <Spinner />}
                    {buttonLabel}
                  </button>
                  <button
                    type="button"
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-darkbg px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-300 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 sm:mt-0 sm:w-auto"
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
